import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authService, EmailPreferences } from '../api/authService';
import '../styles/email-verification.css';
import '../styles/email-preferences.css';

type Status = 'loading' | 'ready' | 'invalid';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#e05252" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// Unauthenticated "manage email preferences" page — linked from every
// reminder email footer via a long-lived, opaque token (see
// UserRepository.EnsureEmailPrefsToken on the backend). Deliberately works
// without logging in, since the whole point is that a logged-out recipient
// can stop reminder emails without first signing back into the app.
const EmailPreferencesPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [status, setStatus] = useState<Status>('loading');
    const [prefs, setPrefs] = useState<EmailPreferences | null>(null);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

    useEffect(() => {
        if (!token) {
            setStatus('invalid');
            return;
        }
        authService.getEmailPreferences(token)
            .then((data) => {
                setPrefs(data);
                setStatus('ready');
            })
            .catch(() => setStatus('invalid'));
    }, [token]);

    const toggle = (key: keyof Omit<EmailPreferences, 'email'>) => {
        if (!prefs) return;
        setPrefs({ ...prefs, [key]: !prefs[key] });
        setSaveStatus('idle');
    };

    const handleSave = async () => {
        if (!prefs) return;
        setSaveStatus('saving');
        try {
            await authService.updateEmailPreferences(token, {
                email_notifications: prefs.email_notifications,
                daily_reminders: prefs.daily_reminders,
                reminders_reviews_due: prefs.reminders_reviews_due,
            });
            setSaveStatus('saved');
        } catch {
            setSaveStatus('error');
        }
    };

    const handleUnsubscribeAll = async () => {
        if (!prefs) return;
        const cleared: EmailPreferences = { ...prefs, email_notifications: false, daily_reminders: false, reminders_reviews_due: false };
        setPrefs(cleared);
        setSaveStatus('saving');
        try {
            await authService.updateEmailPreferences(token, {
                email_notifications: false,
                daily_reminders: false,
                reminders_reviews_due: false,
            });
            setSaveStatus('saved');
        } catch {
            setSaveStatus('error');
        }
    };

    return (
        <div className="ev-page">
            <div className="ev-card">
                {status === 'loading' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--loading">
                            <div className="ev-spinner" />
                        </div>
                        <h1 className="ev-title">Loading preferences&hellip;</h1>
                    </>
                )}

                {status === 'invalid' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><XIcon /></div>
                        <h1 className="ev-title">Link not valid</h1>
                        <p className="ev-body">This email preferences link is invalid or has expired. Log in and visit Settings to manage your notification preferences instead.</p>
                    </>
                )}

                {status === 'ready' && prefs && (
                    <>
                        <h1 className="ev-title">Email preferences</h1>
                        <p className="ev-body">Manage what WeaveWords emails to {prefs.email}.</p>

                        <div className="ep-list">
                            <label className="ep-row">
                                <div>
                                    <div className="ep-row-title">All email notifications</div>
                                    <div className="ep-row-desc">Turn this off to stop every email below at once.</div>
                                </div>
                                <input
                                    type="checkbox"
                                    className="ep-toggle"
                                    checked={prefs.email_notifications}
                                    onChange={() => toggle('email_notifications')}
                                />
                            </label>

                            <label className="ep-row">
                                <div>
                                    <div className="ep-row-title">Daily practice reminders</div>
                                    <div className="ep-row-desc">A nudge to open the app and practice.</div>
                                </div>
                                <input
                                    type="checkbox"
                                    className="ep-toggle"
                                    checked={prefs.daily_reminders}
                                    onChange={() => toggle('daily_reminders')}
                                    disabled={!prefs.email_notifications}
                                />
                            </label>

                            <label className="ep-row">
                                <div>
                                    <div className="ep-row-title">Reviews-due reminders</div>
                                    <div className="ep-row-desc">Sent when you have spaced-repetition reviews waiting.</div>
                                </div>
                                <input
                                    type="checkbox"
                                    className="ep-toggle"
                                    checked={prefs.reminders_reviews_due}
                                    onChange={() => toggle('reminders_reviews_due')}
                                    disabled={!prefs.email_notifications}
                                />
                            </label>
                        </div>

                        <div className="ev-actions">
                            <button className="ev-btn" onClick={handleSave} disabled={saveStatus === 'saving'}>
                                {saveStatus === 'saving' ? 'Saving…' : 'Save preferences'}
                            </button>
                            <button className="ev-btn ev-btn--secondary" onClick={handleUnsubscribeAll} disabled={saveStatus === 'saving'}>
                                Unsubscribe from all
                            </button>
                            {saveStatus === 'saved' && <p className="ev-body ev-body--muted">Preferences saved.</p>}
                            {saveStatus === 'error' && <p className="ev-body ev-body--muted">Something went wrong — please try again.</p>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailPreferencesPage;
